import {formatCopy} from 'utils/typography';
import React, {useState, useEffect, useRef, SyntheticEvent} from "react";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import cx from "classnames";
import { reachGoal } from 'utils/metrica';

import isEmpty from "lodash/isEmpty";
import get from "lodash/get";

import { Delete as DeleteIcon } from '@vedmarket/ui-kit';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import { Save as SaveIcon } from '@vedmarket/ui-kit';
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import { COLORS } from 'config/constants';

import PackageItem from '../package_item'
import CommonPackage from '../common_package'
import CommonFields from './../common_fields'
import PalletesFields from '../palletes_fields'

import {
  useCreateProductMutation,
  useLazyProductByIdQuery,
  useSummaryByOrderIdQuery,
  useUpdateProductMutation
} from "../../../../../../../../../../../../../api/calc";
import { useLazyGetCountryQuery } from "../../../../../../../../../../../../../api/dictionary";
import Notification, {NOTIFICATION_TYPE} from "../../../../../../../../../../../../common/notification";
import {useGetCalcIdFromRoute} from "../../../../../../../../../../../../../utils/hooks";

import {
  getCommonFields,
  getCountries,
  getExciseItemFields,
  getPackageItemFields, getPalletFields,
  getVehicleFields,
  isVehicleHsCode,
  PACKAGE_FORM,
  PackageForm
} from "./helpers";
import RowCode from "../row_code";

import './add_form.scss'
import Chat from "../chat";
import Manufacturer from "../manufacturer";
import {
  getDemoProductData,
  useDemoProductForm,
} from "components/cabinet/demo/steps/packaging/use-product-form";
import {isCalculationViewModeError} from 'utils/helpers';
import {LinkPopover} from '@vedmarket/ui-kit';

const FIELDS_WITHOUT_PRODUCTS_TABLE_UPDATE = [
  PACKAGE_FORM.DESCRIPTION,
  PACKAGE_FORM.MANUFACTURER,
  PACKAGE_FORM.PRODUCT_PHOTO,
];

type Props = {
  invoice?: InvoiceParamsOutput
  count?: number
  id?: number
  product?: ProductParamsOutput
  hideClearButton?: boolean
  insideModal?: boolean
  showErrorNotificationProduct?: boolean
  isFirstInvoice?: boolean
  shouldDisableModalScroll?: boolean
  testDriveScenarioSlug?: string
  handleSuccess(importantFieldsHaveChanged?: boolean): void
  createInvoice?(data: ProductParams): void
  handleRemove?(): void
  onDirtyChange?(isDirty: boolean): void
  saveRequestToken?: number
  setShouldDisableModalScroll?(shouldDisable?: boolean): void
}

type OptionData = {
  code: string
  label: string
  id: string
}

const AddForm = ( {
  invoice,
  count,
  createInvoice,
  handleSuccess,
  id,
  product,
  hideClearButton,
  insideModal,
  showErrorNotificationProduct,
  handleRemove,
  onDirtyChange,
  saveRequestToken,
  testDriveScenarioSlug
} : Props ) => {

  const { t } = useTranslation()

  const [ showErrorNotification, setShowErrorNotification ] = useState<boolean | string | undefined>(showErrorNotificationProduct || false);

  const [ manufacturerVisible, setManufacturerVisible ] = useState(false);
  const [ priceMode, setPriceMode ] = useState('unit_price');
  const [ isCommonPackage, setIsCommonPackage ] = useState(false);

  const orderId = useGetCalcIdFromRoute()

  const addProductRef = useRef<HTMLInputElement>(null);
  const prevSaveRequestToken = useRef(saveRequestToken);

  const { refetch: refetchSummary } = useSummaryByOrderIdQuery(orderId, {
    skip: !orderId,
  })

  const [ defaultCountry, setDefaultCountry ] = useState<OptionData>()
  const [ productPhoto, setProductPhoto ] = useState<ProductPhoto>()

  const [ isAdditional, setIsAdditional ] = useState(false);

  const [ hasPalletes, setHasPalletes ] = useState(false);
  const [ shouldSubmitAfterCalculatedFieldsUpdate, setShouldSubmitAfterCalculatedFieldsUpdate ] = useState(false);

  const methods = useForm<PackageForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const { register, getValues, setValue, watch, trigger, handleSubmit, clearErrors, setError, reset, resetField, formState: { dirtyFields, errors } } = methods

  const isUpdatingCalculatedFields = watch(PACKAGE_FORM.IS_UPDATING_CALCULATED_FIELDS);

  useEffect(() => {

    setShowErrorNotification(showErrorNotificationProduct);

  }, [ showErrorNotificationProduct ])

  const [ productRequest ] = useLazyProductByIdQuery()

  const [ countriesRequest ] = useLazyGetCountryQuery()

  const applyProductData = (productData?: ProductParamsOutput) => {
    if (!productData) {
      return;
    }

    const productDataToApply = getDemoProductData(productData, getValues());
    const normalizedProductData = {
      ...productDataToApply,
      [PACKAGE_FORM.PLACES_MANUAL]: get(productData, PACKAGE_FORM.PLACES_MANUAL, productData?.places_total),
      [PACKAGE_FORM.PRICE]: get(productData, PACKAGE_FORM.PRICE, productData?.price_total),
      [PACKAGE_FORM.PRODUCT_PHOTO]: productData?.product_photo?.id || null,
    };

    // @ts-ignore
    reset(normalizedProductData)

    setIsCommonPackage(Boolean(productData?.[PACKAGE_FORM.VOLUME_MANUAL]))
    setIsAdditional(Boolean(productData?.[PACKAGE_FORM.EXCISE_RATE]?.description) || isVehicleHsCode(productData?.code))
    setHasPalletes(Boolean(productData?.[PACKAGE_FORM.PALLETE_TYPE]))
    setProductPhoto(productData?.product_photo)

    if (productData.country_data) {
      setDefaultCountry({
        code: productData.country_data.code,
        label: productData.country_data.name,
        id: productData.country_data.code,
      });
    } else {
      const countryCode = productData.country_code;

      if (countryCode) {
        countriesRequest({search: countryCode})?.then((response) => {
          // @ts-ignore
          response?.data && setDefaultCountry(
            getCountries(response.data).find(country => country.code === countryCode || country.id === countryCode)
          )
        });
      }
    }

    trigger();
  }

  useEffect(() => {
    if (product && product.id === id) {
      applyProductData(product);
      return;
    }

    if (id && invoice?.id) {
      productRequest({
        invoiceId: invoice.id,
        productId: id,
      }).then((productData) => {
        // @ts-ignore
        applyProductData(productData.data)
      })
    }
  }, [ id, invoice?.id, product ])

  // const defaultCountryCode = getValues()[PACKAGE_FORM.COUNTRY_CODE]

  // useEffect(() => {
  //   console.log(defaultCountryCode);
  //   if (defaultCountryCode) {
  //     countriesRequest({search: defaultCountryCode})?.then((response) => {
  //       response?.data && setDefaultCountry(getCountries(response.data).find(country => country.code === defaultCountryCode))
  //     });
  //   }
  // }, [ defaultCountryCode ])

  const [ createProduct ] = useCreateProductMutation()

  const [ updateProduct ] = useUpdateProductMutation()

  const getShouldRefetchRequests = () =>
    Object.keys(dirtyFields).some(
      (fieldName) => !FIELDS_WITHOUT_PRODUCTS_TABLE_UPDATE.includes(fieldName as PACKAGE_FORM)
    )

  const demoPlayback = useDemoProductForm({
    containerRef: addProductRef,
    dirtyFields,
    getValues,
    onError: setShowErrorNotification,
    onSuccess: () => handleSuccess(getShouldRefetchRequests()),
    product,
    scenarioSlug: testDriveScenarioSlug,
  })

  const isDemoMode = demoPlayback.isDemoMode

  const checkErrors = (isSuccess: boolean, field: any, errors?: any) => {
    if (isSuccess) {
      clearErrors(field)
      return
    }

    errors[field]?.[0] ? setError(field, { type: 'custom', message: errors[field]?.[0]} ) : clearErrors(field)

  }

  const getPackageData = (values: PackageForm) => {

    const commonFields = getCommonFields(values)

    const packageItemFields = getPackageItemFields(values)

    const commonPackageFields = {
      [PACKAGE_FORM.VOLUME_MANUAL]: values[PACKAGE_FORM.VOLUME_MANUAL],
      [PACKAGE_FORM.NET_WEIGHT_MANUAL]: values[PACKAGE_FORM.NET_WEIGHT_MANUAL],
      [PACKAGE_FORM.GROSS_WEIGHT_MANUAL]: values[PACKAGE_FORM.GROSS_WEIGHT_MANUAL],
    }

    const dataToSend = {
      ...commonFields,
      ...(isCommonPackage && commonPackageFields),
      ...(!isCommonPackage && packageItemFields),
      ...(isAdditional && getExciseItemFields(values)),
      ...(isVehicleHsCode(values[PACKAGE_FORM.CODE]) && getVehicleFields(values)),
      ...(hasPalletes && getPalletFields(values)),
    }

    if (!hasPalletes) {
      delete dataToSend[PACKAGE_FORM.PALLETE_TYPE];
      delete dataToSend[PACKAGE_FORM.PALLETE_CNT];
      delete dataToSend[PACKAGE_FORM.PALLETE_HEIGHT];
      delete dataToSend[PACKAGE_FORM.PALLETE_WEIGHT];
      delete dataToSend[PACKAGE_FORM.TWO_TIERS_ALLOWED];
    }

    if (!isAdditional) {
      delete dataToSend[PACKAGE_FORM.EXCISE_VALUE];
      delete dataToSend[PACKAGE_FORM.EXCISE_RATE];
    }

    return dataToSend;
  }

  const handleSend = async(values: PackageForm) => {
    if (await demoPlayback.handleSend(values)) {
      return;
    }

    const dataToSend = getPackageData(values);

    if (createInvoice) {
      createInvoice(dataToSend);
      return;
    }

    if (!invoice?.id){
      return;
    }

    const result = id ?
      await updateProduct({ invoiceId: invoice.id, productId : id, ...dataToSend})
      : await createProduct({ invoiceId: invoice.id, ...dataToSend});

    if (isCalculationViewModeError(result)) {
      return;
    }

    // @ts-ignore
    const errorData = result?.error?.data
    const productId = get(result, 'data.id', '')

    checkErrors(Boolean(productId), PACKAGE_FORM.LENGTH, errorData)
    checkErrors(Boolean(productId), PACKAGE_FORM.WIDTH, errorData)
    checkErrors(Boolean(productId), PACKAGE_FORM.HEIGHT, errorData)
    checkErrors(Boolean(productId), PACKAGE_FORM.NET_WEIGHT_CARTOON, errorData)
    checkErrors(Boolean(productId), PACKAGE_FORM.GROSS_WEIGHT_CARTOON, errorData)

    if (!productId || errorData) {
      setShowErrorNotification(errorData?.non_field_errors?.[0] || true);
      return
    }
    setShowErrorNotification(false);

    handleSuccess(getShouldRefetchRequests())
    refetchSummary()
    reset()

  }

  const checkIsValid = () => {
    // Для авто без акциза достаточно параметров ТС
    if (isAdditional && isVehicleHsCode(getValues(PACKAGE_FORM.CODE)) && !getValues(PACKAGE_FORM.EXCISE_RATE)) {
      return Boolean(getValues(PACKAGE_FORM.ENGINE_TYPE) && getValues(PACKAGE_FORM.ENGINE_POWER_HP)) && isEmpty(errors);
    }
    const exciseRateIsZero = getValues(PACKAGE_FORM.EXCISE_RATE)?.Value === '0';
    // @ts-ignore
    return isAdditional ? getValues(PACKAGE_FORM.EXCISE_RATE) && (getValues(PACKAGE_FORM.EXCISE_VALUE) || getValues(PACKAGE_FORM.EXCISE_VALUE) === 0 || ( exciseRateIsZero && (getValues(PACKAGE_FORM.EXCISE_VALUE) === '' || !getValues(PACKAGE_FORM.EXCISE_VALUE)))) && isEmpty(errors): isEmpty(errors);
  }

  const onClear = () => {
    reset();
    setIsAdditional(false);
  }

  const onSubmit = handleSubmit(values => handleSend(values))

  useEffect(() => {
    if (!isUpdatingCalculatedFields && shouldSubmitAfterCalculatedFieldsUpdate) {
      setShouldSubmitAfterCalculatedFieldsUpdate(false);
      onSubmit();
    }
  }, [ isUpdatingCalculatedFields, shouldSubmitAfterCalculatedFieldsUpdate ])

  const handleClickSubmit = () => {
    const values = getValues();
    const fields: (keyof PackageForm)[] = [
      PACKAGE_FORM.NAME, PACKAGE_FORM.PIECES, PACKAGE_FORM.PLACES_MANUAL,
      PACKAGE_FORM.PIECES_IN_CARTOON,
      ...(priceMode === 'unit_price' ? [ PACKAGE_FORM.UNIT_PRICE ] as const : [ PACKAGE_FORM.PRICE ] as const),
      ...((!id || defaultCountry) ? [ PACKAGE_FORM.COUNTRY_CODE, PACKAGE_FORM.CODE ] as const : []),
      ...(manufacturerVisible ? [ PACKAGE_FORM.DESCRIPTION, PACKAGE_FORM.MANUFACTURER ] as const : []),
      ...(isCommonPackage
        ? [ PACKAGE_FORM.VOLUME_MANUAL, PACKAGE_FORM.NET_WEIGHT_MANUAL, PACKAGE_FORM.GROSS_WEIGHT_MANUAL ] as const
        : [ PACKAGE_FORM.LENGTH, PACKAGE_FORM.WIDTH, PACKAGE_FORM.HEIGHT, PACKAGE_FORM.NET_WEIGHT_CARTOON, PACKAGE_FORM.GROSS_WEIGHT_CARTOON ] as const),
      ...(hasPalletes ? [ PACKAGE_FORM.PALLETE_TYPE, PACKAGE_FORM.PALLETE_CNT, PACKAGE_FORM.PALLETE_HEIGHT, PACKAGE_FORM.PALLETE_WEIGHT, PACKAGE_FORM.TWO_TIERS_ALLOWED ] as const : []),
      ...(isAdditional && !isVehicleHsCode(values.code) && values.excise_value ? [ PACKAGE_FORM.EXCISE_VALUE ] as const : []),
      ...(isAdditional && isVehicleHsCode(values.code) ? [ PACKAGE_FORM.ENGINE_TYPE, PACKAGE_FORM.ENGINE_POWER_HP, PACKAGE_FORM.ENGINE_VOLUME_CC, PACKAGE_FORM.MANUFACTURE_DATE ] as const : []),
    ];
    const visibleValues = Object.fromEntries(fields.map(field => [ field, values[field] ]));
    reachGoal({
      goal: '2_tovar',
      params: {
        ...visibleValues,
        common_package: isCommonPackage,
        palletes: hasPalletes,
        price_mode: priceMode,
        ...(isAdditional && !isVehicleHsCode(values.code) && { excise_rate: values.excise_rate?.Value }),
      },
    });

    if (isUpdatingCalculatedFields) {
      setShouldSubmitAfterCalculatedFieldsUpdate(true);
      return;
    }

    onSubmit();
  }

  const handleFormChange = () => {
    onDirtyChange && onDirtyChange(true);
  }

  useEffect(() => {
    onDirtyChange && onDirtyChange(Boolean(Object.keys(dirtyFields).length));
  }, [ dirtyFields, onDirtyChange ])

  useEffect(() => {
    if (saveRequestToken && prevSaveRequestToken.current !== saveRequestToken) {
      handleClickSubmit();
    }

    prevSaveRequestToken.current = saveRequestToken;
  }, [ saveRequestToken ])

  const handleChangeSwitch = (event: SyntheticEvent<Element, Event>, checked:boolean) => {
    if (isDemoMode) {
      return
    }

    if (checked) {
      clearErrors(PACKAGE_FORM.LENGTH);
      clearErrors(PACKAGE_FORM.WIDTH);
      clearErrors(PACKAGE_FORM.HEIGHT);
      clearErrors(PACKAGE_FORM.NET_WEIGHT_CARTOON);
      clearErrors(PACKAGE_FORM.GROSS_WEIGHT_CARTOON);

      resetField(PACKAGE_FORM.LENGTH);
      resetField(PACKAGE_FORM.WIDTH);
      resetField(PACKAGE_FORM.HEIGHT);
      resetField(PACKAGE_FORM.NET_WEIGHT_CARTOON);
      resetField(PACKAGE_FORM.GROSS_WEIGHT_CARTOON);
    } else {
      clearErrors(PACKAGE_FORM.VOLUME_MANUAL);
      clearErrors(PACKAGE_FORM.NET_WEIGHT_MANUAL);
      clearErrors(PACKAGE_FORM.GROSS_WEIGHT_MANUAL);

      resetField(PACKAGE_FORM.VOLUME_MANUAL);
      resetField(PACKAGE_FORM.NET_WEIGHT_MANUAL);
      resetField(PACKAGE_FORM.GROSS_WEIGHT_MANUAL);
    }
    setIsCommonPackage(checked);
  }

  const isSaveButtonDisabled = !isDemoMode && !checkIsValid()

  const cargoModeHelpKey = isCommonPackage
    ? 'calc__help__cargoModeEnabled'
    : 'calc__help__cargoModeDisabled';

  return <>
    <div className='add-product' ref={addProductRef} onChange={handleFormChange} onInput={handleFormChange}>
      <FormProvider {...methods}>
        <div className={cx({
          'add-product__form': true,
          'add-product__form_isCommon': isCommonPackage,
          'add-product__form_hasPalletes': hasPalletes,
          'add-product__form_isAdditional': isAdditional,
        })}>
          <div className='product-field product-field_name'>
            <TextField
              className={`add-product__${PACKAGE_FORM.NAME}`}
              {...register(PACKAGE_FORM.NAME, {
                required: true,
                maxLength: { value: 200, message: t('common__text__only200Symbols') },
              })}
              error={Boolean(errors[PACKAGE_FORM.NAME])}
              helperText={errors[PACKAGE_FORM.NAME]?.message}
              variant='outlined'
              label={t('calc__text__packaging__productNameLabel')}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                sx: { paddingLeft: '12px' },
                startAdornment: count ? <InputAdornment position='start' sx={{ marginRight: '4px' }}>
                  <Typography variant='body1' sx={{ color: COLORS.LIGHT_TEXT_DISABLED }}>
                    {count}.
                  </Typography>
                </InputAdornment> : undefined,
              }}
              inputProps={{ style: { paddingLeft: 0 } }}
              disabled={demoPlayback.fieldDisabled}
            />
            <LinkPopover
              linkText={t('calc__help__productNameLink')}
              text={formatCopy(t('calc__help__productName'))}
            />
          </div>
          <Manufacturer onVisibilityChange={setManufacturerVisible} initialPhoto={productPhoto} disabled={demoPlayback.fieldDisabled} />
          {id && defaultCountry && <RowCode
            defaultCountry={defaultCountry}
            setAdditional={(value) => setIsAdditional(value)}
            isAdditional={isAdditional}
            id={id}
            invoiceId={invoice?.id}
            insideModal={insideModal}
            addProductRef={addProductRef}
          />}
          {!id && <RowCode
            setAdditional={(value) => setIsAdditional(value)}
            isAdditional={isAdditional}
            id={id}
            invoiceId={invoice?.id}
            insideModal={insideModal}
            addProductRef={addProductRef}
          />}
          <CommonFields onPriceModeChange={setPriceMode} isDemoRecalculating={demoPlayback.isRecalculating} />
          <div className='product-field product-field_toggle product-field_cargo-mode'>
            <FormControlLabel
              className={`add-product__common_package`}
              checked={isCommonPackage}
              control={<Switch
                color='primary'
                disabled={demoPlayback.fieldDisabled}
                sx={{ letterSpacing: 0}}
              />}
              onChange={handleChangeSwitch}
              label={<Typography variant='body2'>{t('common__text__totalDimensionsOfTheLoad')}</Typography>}
              sx={{ margin: 0 }}
            />
            <LinkPopover
              linkText={t(`${cargoModeHelpKey}Link`)}
              text={formatCopy(t(cargoModeHelpKey))}
            />
          </div>
          <div className='product-field product-field_toggle product-field_pallet-mode'>
            <FormControlLabel
              className={`add-product__${PACKAGE_FORM.PALLETES}`}
              {...register(PACKAGE_FORM.PALLETES)}
              control={<Checkbox checked={hasPalletes} disabled={demoPlayback.fieldDisabled} />}
              onChange={() => {
                if (isDemoMode) {
                  return
                }

                setHasPalletes(!hasPalletes);
                trigger([ PACKAGE_FORM.PALLETES ])
              }}
              label={<Typography variant='body2'>{t('calc__text__packaging__calculationByPallet')}</Typography>}
              sx={{ margin: 0 }}
            />
            <LinkPopover
              linkText={t('calc__help__palletUsageLink')}
              text={formatCopy(t('calc__help__palletUsage'))}
            />
          </div>
          {isCommonPackage ? <CommonPackage /> : <PackageItem />}
          {hasPalletes && <PalletesFields />}
          <div className='add-product__row-buttons'>
            <div className='add-product__buttons-container'>
              <IconButton
                aria-label={t('actions__text__delete')}
                sx={{ color: COLORS.LIGHT_TEXT_DISABLED_OPACITY, width: '32px', height: '32px', padding: '4px' }}
                onClick={handleRemove}
                disabled={!handleRemove}
              >
                <DeleteIcon />
              </IconButton>
              {!hideClearButton && <Button
                variant='outlined'
                fullWidth
                onClick={onClear}
              ><Typography variant='button'>{t('actions__text__clear')}</Typography></Button>}
            </div>
            <Button
              variant='contained'
              fullWidth
              onClick={handleClickSubmit}
              startIcon={<SaveIcon />}
              disabled={isSaveButtonDisabled || demoPlayback.isRecalculating || demoPlayback.saveButtonDisabled}
              {...demoPlayback.saveButtonProps}
              className='add-product__addButton'
            ><Typography variant='button'>{`${t('actions__text__save')} ${t('common__text__addItem')}`}</Typography></Button>
          </div>
        </div>
      </FormProvider>
      {demoPlayback.loader}
      {showErrorNotification &&
          <Notification
            type={NOTIFICATION_TYPE.ERROR}
            title={t('errors__message__save')}
            text={typeof showErrorNotification === 'string' ?  showErrorNotification : t('errors__message__tryLater5Min')}
            sx={{ marginTop: 4 }}/>}
    </div>
  </>
}

export default AddForm
